import React, { useState } from 'react';
import { Card } from '../../../../../../components/Card/Card';
import { Form, useChanges } from '../../../../../../components/Form/Form';
import { TextField } from '../../../../../../components/Form/TextField/TextField';
import { Icon } from '../../../../../../components/Icon/Icon';
import { KeyValueTable } from '../../../../../../components/KeyValueTable/KeyValueTable';
import { ControlledModal, ModalController } from '../../../../../../components/Modal/ControlledModal';
import { Stack } from '../../../../../../components/Stack/Stack';
import { t } from '../../../../../../locales/translate';
import { Event, updateEventDetails, useEventSalesStatusPolling } from '../../../../../../model/event';
import { handleError } from '../../../../../../model/notification';
import './Status.css';
import { StatusDraft } from './StatusDraft';
import { StatusInReview } from './StatusInReview';
import { StatusOnSale } from './StatusOnSale';

interface Props {
  event: Event;
}

export function Status({ event }: Props): JSX.Element {
  const [openModal, setOpenModal] = useState(false);
  useEventSalesStatusPolling(event.id, { start: true });

  return (
    <div>
      <Card title="Status">
        <Stack className="event-status">
          {event.status === 'draft' && <StatusDraft event={event} />}
          {event.status === 'in_review' && <StatusInReview event={event} />}
          {(event.status === 'on_sale' || event.status === 'stopped') && <StatusOnSale event={event} />}
          <div className="event-status__sold-out-msg">
            <KeyValueTable
              items={[
                {
                  key: t.model.event.soldOutMsg,
                  labelClass: 'uppercase',
                  value: (
                    <span>
                      <span>{event.soldOutMessage}</span>
                      {event.$permissions.includes('update_sold_out_message') && (
                        <Icon onClick={() => setOpenModal(true)} name="edit" />
                      )}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </Stack>
      </Card>
      <ControlledModal<{ soldOutMessage?: string }>
        open={openModal}
        model={{ soldOutMessage: event.soldOutMessage }}
        onClose={() => setOpenModal(false)}
        title={t.model.event.updateSoldOutMsg}
        size="small"
      >
        {(controller) => <SoldOutForm controller={controller} eventId={event.id} />}
      </ControlledModal>
    </div>
  );
}

function SoldOutForm({
  controller,
  eventId,
}: {
  controller: ModalController<{ soldOutMessage?: string }>;
  eventId: string;
}): JSX.Element {
  const [changes, , model, trackChanges] = useChanges<{
    soldOutMessage?: string;
  }>(controller.model);

  async function handleOnSubmit(): Promise<void> {
    try {
      await updateEventDetails({ eventId, changes });
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });
        return (
          <>
            <TextField label={t.model.event.soldOutMsg} name="soldOutMessage" nullIfEmpty />
          </>
        );
      }}
    </Form>
  );
}
